//many of these utility types must use any
/*eslint-disable @typescript-eslint/no-explicit-any*/

/**
 * Returns a tuple without the first element.
 * Taken from: https://stackoverflow.com/a/62253741
 */
export type Tail<T extends readonly any[]> = ((...t: T) => void) extends (
    h: any,
    ...r: infer R
) => void
    ? R
    : never;
/**
 * Returns the last element of a tuple.
 * Taken from: https://stackoverflow.com/a/62253741
 */
export type Last<T extends readonly any[]> = number extends T['length']
    ? T[1e100]
    : {
          [K in keyof T]: K extends keyof Tail<T> ? never : T[K];
      }[number];
/**
 * Returns a tuple without the last element.
 * Taken from: https://stackoverflow.com/a/63790089
 */
export type Head<T extends any[]> = T extends [...infer Head, any] ? Head : any[];

/**
 * Represents a callback method typically used by a function that implement the async callback
 * pattern. If the asynchronous method fails, the callback is invoked with a non-null err
 * parameter. If the asynchronous method succeeds, the callback is invoked with a null err value
 * and result defined.
 */
export type AsyncCallback<T> = (err: any | null, result?: T) => void;
/**
 * Represents a function that implements the asynchronous callback pattern.
 */
export type AsyncCallbackStyleMethod<
    TResult,
    TInArgs extends any[],
    TArgs extends any[] = [...TInArgs, AsyncCallback<TResult>]
> = (...args: TArgs) => void;

/**
 * Given a function that implements the asynchronous callback pattern, returns a function type that
 * implements the corresponding async/await pattern.
 */
export type AsPromised<
    TFunc extends (...args: any[]) => any,
    TParams extends any[] = Parameters<TFunc>,
    TCallback extends AsyncCallback<any> = Last<TParams>
> = TCallback extends AsyncCallback<any>
    ? (...args: Head<TParams>) => Promise<Last<Parameters<TCallback>>>
    : never;

/**
 * Converts a function which implements the asynchronous callback pattern to an async function.
 */
export function Promisify<TReturn, TArgs extends any[]>(
    func: AsyncCallbackStyleMethod<TReturn, TArgs>
): (...args: TArgs) => Promise<TReturn> {
    //I attempted to return AsPromised<typeof func>, but failed to get the types to align. perhaps
    //someone smarter than me can get it to work?

    return function (...args: TArgs) {
        return new Promise<TReturn>((resolve, reject) => {
            func(...args, (err: unknown | null, result: TReturn | undefined) => {
                if (typeof err !== 'undefined' && null !== err) {
                    reject(err);
                } else {
                    resolve(result as TReturn);
                }
            });
        });
    };
}

/**
 * Type which converts all members of the provided class from asynchronous callback style methods
 * to async functions.
 */
export type PromisedAll<T extends Record<string, (...args: any[]) => any>> = {
    [x in keyof T]: AsPromised<T[x]>;
};
